import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { pluralReadableTypeName } from '@iotv/core-services';
import { AdjacentType, getSimpleBranchingQuery, getSimpleSubjectQuery, getSpecificEdgeTypeId } from '@iotv/datamodel-core';
import { AppValueTypes, ComparisonType } from '@iotv/iotv-v3-types';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Stack, Tab } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { getAreasUnderCustomerQueryDef } from '../../../helpers/queryHelpers';
import { useMostProximateCustomer } from '../../../hooks/useMostProximateCustomer';
import { useTabHandlerStringKeys } from '../../../hooks/useTabHandler';
import { Severity } from '../../../types';
import { ShowDefinedEntriesOnlyOpts } from '../../../types/AppTypes';
import { ObjectDetailCard } from '../../../v2/components/generic/ObjectView/ObjectDetailCard';
import { ObjectHeader } from '../../../v2/components/generic/ObjectView/ObjectHeader';
import { AutoTab, getAreaViewDefinition } from '../../PropertyArea';
import { getViewDefintionFromObject } from '../../factories/AttributeDefinitions';
import { Listerlizer } from '../../factories/Listerlizer/Listerlizer';
import { EntityGroupPartnerByRegionTab } from './EntityGroupPartnerByRegionTab';
import { EntityGroupPartnerTab } from './EntityGroupPartnerTab';
const debug = process.env.REACT_APP_DEBUG && false;
export const EntityGroupView = (props) => {
    const { transactionFunctions, viewObject, additionalTabs, listTabDefinition, contextCustomer, userFunctions, applicationData, allowedActions } = props;
    const contextObject = viewObject.matchedPrimary;
    const mostProximateCustomer = useMostProximateCustomer({ item: contextObject });
    const [groupViewDefinition, setGroupViewDefinition] = useState(contextObject
        ? getViewDefintionFromObject(contextObject)
        : {
            name: {
                key: 'name',
                editable: false,
                label: 'name',
                precision: undefined,
                stateFn: undefined,
                type: AppValueTypes.STRING,
                unit: undefined,
                validationRx: undefined,
            },
        });
    const supportedTypeViewDefinition = contextObject?.supportedType ? getViewDefintionFromObject({ type: contextObject.supportedType }) : undefined;
    const filteredSupportedTypeViewDefinition = supportedTypeViewDefinition
        ? Object.fromEntries(Object.entries(supportedTypeViewDefinition).filter(([key, def]) => def.hideFromList !== true))
        : undefined;
    useEffect(() => {
        if (contextObject)
            setGroupViewDefinition(getViewDefintionFromObject(contextObject));
    }, [contextObject]);
    const getEntityGroupContext = (g) => {
        const egCtxQuery = getSimpleSubjectQuery('Group');
        egCtxQuery.addSubQuery(getSimpleBranchingQuery('Group', 'Group', AdjacentType.CHILD));
        egCtxQuery.addSubQuery(getSimpleBranchingQuery('Group', 'Place', AdjacentType.CHILD));
        egCtxQuery.addSubQuery(getSimpleBranchingQuery('Group', 'Area', AdjacentType.PARENT));
        egCtxQuery.addSubQuery(getSimpleBranchingQuery('Group', 'Property', AdjacentType.PARENT));
        egCtxQuery.addSubQuery(getSimpleBranchingQuery('Group', 'Partner', AdjacentType.PARENT));
        ConsumerDao.getGraphQueryRequestV2({ query: egCtxQuery, contextObjects: [g] }).then((res) => {
            if (res.err || !res.data) {
                userFunctions.setUserMessage({
                    id: v4(),
                    label: 'Failed to load Group',
                    type: 'MessageBar',
                    content: `There was an error loading ${g.name}.${debug ? ' Please check the developer console for more details.' : ''}`,
                    severity: Severity.error,
                });
                console.error(res.err ? res.err.message : 'EntityGroupViewV2: No err but no data on graph query res');
                return;
            }
            userFunctions.addObjectsToState(res.data.getContainedNodesAndEdges());
        });
    };
    useEffect(() => {
        if (contextObject)
            getEntityGroupContext(contextObject);
    }, [contextObject?.sk]);
    const DefaultTabs = contextObject && contextObject.supportedType
        ? [
            {
                label: contextObject.supportedType ? pluralReadableTypeName(contextObject.supportedType) : 'Entities',
                value: 'entities',
                type: contextObject.supportedType,
                viewDefinition: listTabDefinition ?? filteredSupportedTypeViewDefinition,
            },
            {
                label: `Sub Groups`,
                value: 'subgroups',
                type: 'Group',
                constraints: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: `${contextObject.supportedType}` }],
            },
            {
                label: `Property`, // Cardinality is one!
                value: 'property',
                type: 'Property',
                direction: AdjacentType.PARENT,
            },
            // TODO: would be good to have fullnames here
            {
                label: `Areas`,
                value: 'areas',
                component: (_jsx(Listerlizer, { viewObject: viewObject, viewDefinition: getAreaViewDefinition(), adjacentFilter: { adjacentType: AdjacentType.PARENT, objectType: 'Area' }, contextObject: contextCustomer, searchRootObjects: contextCustomer ? [contextCustomer] : undefined, linkableItemsBranchingQueryDefinition: getAreasUnderCustomerQueryDef(), userFunctions: userFunctions, transactionFunctions: transactionFunctions })),
            },
            {
                label: `Visible to User Group`,
                value: 'visibleuser',
                type: 'Group',
                constraints: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: `User` }],
                direction: AdjacentType.PARENT,
                edgeTypeId: getSpecificEdgeTypeId('Group', 'Group', 'group_allows_group'),
            },
            {
                label: `Shared with Partners`,
                value: 'partners',
                component: _jsx(EntityGroupPartnerTab, { userFunctions, transactionFunctions, viewObject, applicationData }),
            },
            {
                label: `Shared with Partners in Region`,
                value: 'partnersregion',
                component: _jsx(EntityGroupPartnerByRegionTab, { userFunctions, transactionFunctions, viewObject, applicationData }),
            },
        ]
        : [];
    // NOTE: (ADH) i really don't like this.
    const [appContext, dispatch] = useContext(AppMainViewContext);
    /** all the externally-defined tabs */
    const externalTabDefinitions = Object.entries(additionalTabs ?? {}).map(([tabName, Komponent]) => ({
        label: tabName,
        value: tabName,
        component: Komponent ? _jsx(Komponent, { ...appContext, viewObject, allowedActions }) : null,
    }));
    const findInternalTabByName = (name) => Object.values(DefaultTabs).find(({ label }) => label === name);
    const findExternalTabByName = (name) => Object.values(externalTabDefinitions).find(({ label }) => label === name);
    /** tabs that are in addition to the base LSC tabs */
    const additionalTabDefinitions = Object.values(externalTabDefinitions).filter(({ label }) => !findInternalTabByName(label));
    /** tabs that will override base LSC tabs (including with `null`, thus effectively hiding / disabling them) */
    const overriddenTabDefinitions = Object.values(DefaultTabs).map((tab) => findExternalTabByName(tab.label) ?? tab);
    const tabDefs = [...additionalTabDefinitions, ...overriddenTabDefinitions];
    const tabsToDisplay = Object.fromEntries(tabDefs.filter((tabDef) => tabDef.component !== null).map((t) => [t.value, t]));
    const { state: [[tabValue, setTabValue]], handlers: { handleTabChange }, } = useTabHandlerStringKeys(tabsToDisplay);
    return (_jsxs(_Fragment, { children: [_jsx(ObjectHeader, { vob: contextObject }), _jsxs(Stack, { gap: 1, children: [contextObject && (_jsx(ObjectDetailCard, { matchedPrimary: contextObject,
                        viewDefinition: groupViewDefinition,
                        definedEntriesOnlyOpt: ShowDefinedEntriesOnlyOpts.Yes,
                        mostProximateCustomer: contextCustomer,
                        userFunctions,
                        transactionFunctions,
                        allowedActions })), _jsxs(TabContext, { value: tabValue, children: [_jsx(TabList, { onChange: handleTabChange, children: Object.values(tabsToDisplay).map((t) => (_jsx(Tab, { label: t.label, value: t.value }, `tab_${t.value}`))) }), Object.values(tabsToDisplay).map((t) => (_jsx(TabPanel, { value: t.value, sx: { padding: 0, paddingBlockStart: 0 }, children: contextObject && (_jsx(AutoTab, { tabDef: t, contextObject: contextObject, contextCustomer: contextCustomer, viewObject: viewObject, userFunctions: userFunctions, transactionFunctions: transactionFunctions, mostProximateCustomer: mostProximateCustomer })) }, `tabpanel_${t.value}`)))] })] })] }));
};
